'use client';
import React, { useEffect, useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';

interface ReviewOption {
  label: string;
  reviews: number;
  avgReward: number;
}

const DEFAULT_OPTIONS: ReviewOption[] = [
  { label: '2 reviews/month', reviews: 2, avgReward: 800 },
  { label: '5 reviews/month', reviews: 5, avgReward: 900 },
  { label: '10 reviews/month', reviews: 10, avgReward: 1000 },
  { label: '20 reviews/month', reviews: 20, avgReward: 1100 },
];
const DEFAULT_FREE_PRODUCT_VALUE = 1500;

interface EarningsCalculatorProps {
  onCTAClick: () => void;
}

export default function EarningsCalculator({ onCTAClick }: EarningsCalculatorProps) {
  const [reviewOptions, setReviewOptions] = useState<ReviewOption[]>(DEFAULT_OPTIONS);
  const [freeProductValue, setFreeProductValue] = useState(DEFAULT_FREE_PRODUCT_VALUE);
  const [selectedIdx, setSelectedIdx] = useState(1);

  useEffect(() => {
    const supabase = createClient();
    Promise.all([
      supabase.from('platform_settings').select('value').eq('key', 'earnings_review_options').maybeSingle(),
      supabase.from('platform_settings').select('value').eq('key', 'earnings_free_product_value').maybeSingle(),
    ]).then(([optRes, fpvRes]) => {
      if (optRes.data?.value) {
        try { setReviewOptions(JSON.parse(optRes.data.value)); } catch {}
      }
      if (fpvRes.data?.value) {
        const n = Number(fpvRes.data.value);
        if (!isNaN(n)) setFreeProductValue(n);
      }
    });
  }, []);

  const selected = reviewOptions[Math.min(selectedIdx, reviewOptions.length - 1)];
  const monthlyEarning = selected.reviews * selected.avgReward;
  const annualEarning = monthlyEarning * 12;
  const freeProductTotal = selected.reviews * freeProductValue;

  return (
    <section className="section-py bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-10">
          <span className="pill-badge mb-3">Earnings Calculator</span>
          <h2 className="font-heading font-bold text-foreground mt-3" style={{ fontSize: 'clamp(1.5rem, 3vw, 2rem)' }}>
            How Much Can You Earn?
          </h2>
          <p className="text-muted-foreground mt-2 text-sm">Select your review frequency to estimate monthly earnings</p>
        </div>

        <div className="bg-white rounded-3xl border border-border shadow-sm p-6 sm:p-8 space-y-8">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {reviewOptions.map((opt, i) => (
              <button
                key={i}
                onClick={() => setSelectedIdx(i)}
                className={`py-3 px-3 sm:px-4 rounded-2xl text-xs sm:text-sm font-semibold font-heading transition-all ${
                  selectedIdx === i
                    ? 'cta-gradient text-white shadow-md shadow-primary/20'
                    : 'bg-muted text-muted-foreground hover:bg-primary-container hover:text-primary'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
            <div className="bg-primary-container rounded-2xl p-6 text-center border border-primary/10">
              <Icon name="CurrencyRupeeIcon" size={24} className="text-primary mx-auto mb-2" />
              <div className="font-heading font-bold text-3xl text-primary">₹{monthlyEarning.toLocaleString('en-IN')}</div>
              <div className="text-xs text-muted-foreground mt-1 font-medium">Monthly Earnings</div>
            </div>
            <div className="bg-green-50 rounded-2xl p-6 text-center border border-green-100">
              <Icon name="CalendarDaysIcon" size={24} className="text-accent mx-auto mb-2" />
              <div className="font-heading font-bold text-3xl text-accent">₹{annualEarning.toLocaleString('en-IN')}</div>
              <div className="text-xs text-muted-foreground mt-1 font-medium">Annual Earnings</div>
            </div>
            <div className="rounded-2xl p-6 text-center border border-secondary/10 bg-secondary-container">
              <Icon name="GiftIcon" size={24} className="text-secondary mx-auto mb-2" />
              <div className="font-heading font-bold text-3xl text-secondary">₹{freeProductTotal.toLocaleString('en-IN')}</div>
              <div className="text-xs text-muted-foreground mt-1 font-medium">Free Products Value</div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-center gap-4 pt-2">
            <button
              onClick={onCTAClick}
              className="cta-gradient text-white font-heading font-bold px-8 py-3.5 rounded-xl hover:opacity-90 transition-opacity text-sm w-full sm:w-auto shadow-sm shadow-primary/20"
            >
              Start Earning Now
            </button>
            <p className="text-xs text-muted-foreground text-center sm:text-left">
              Based on platform averages. Actual earnings may vary by category and review quality.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}